import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useLocation } from '../context/LocationContext';
import { supabase } from '../services/supabase';
import { avatarUrl as av } from '../utils/imageUrl';
import LocationBanner from '../components/LocationBanner';
import {
  FaSearch, FaUsers, FaChevronDown, FaChevronUp,
  FaPhone, FaCalendarAlt, FaQrcode, FaBell, FaTimes, FaPaperPlane,
} from 'react-icons/fa';
import '../styles/CustomersPage.css';

const SEGMENTS = [
  { key: 'all',      label: 'Hamısı' },
  { key: 'inactive', label: 'Uzun müddət gəlməyənlər' },
  { key: 'birthday', label: 'Doğum günü' },
  { key: 'frequent', label: 'Tez-tez gələnlər' },
];

const OCCASION_LABELS = {
  birthday: 'Doğum günü',
  romantic:  'Romantik',
  business:  'İş görüşü',
  family:    'Ailə',
};

const STATUS_STYLE = {
  accepted:  { label: 'Qəbul',      color: '#16a34a', bg: '#f0fdf4' },
  pending:   { label: 'Gözlənilir', color: '#d97706', bg: '#fffbeb' },
  rejected:  { label: 'Rədd',       color: '#dc2626', bg: '#fef2f2' },
  completed: { label: 'Tamamlandı', color: '#6366f1', bg: '#eef2ff' },
  cancelled: { label: 'Ləğv',       color: '#9ca3af', bg: '#f9fafb' },
};

const TIER_STYLE = {
  '5-20':  { background: '#f0fdf4', color: '#16a34a', fontWeight: 700, padding: '2px 8px', borderRadius: 8, fontSize: 12 },
  '20-50': { background: '#fffbeb', color: '#d97706', fontWeight: 700, padding: '2px 8px', borderRadius: 8, fontSize: 12 },
  '50+':   { background: '#fef2f2', color: '#dc2626', fontWeight: 700, padding: '2px 8px', borderRadius: 8, fontSize: 12 },
};

const DAY_MS = 86400000;

const CustomersPage = () => {
  const { business } = useAuth();
  const { locationId } = useLocation();
  const [customers, setCustomers]   = useState([]);
  const [loading, setLoading]       = useState(true);
  const [search, setSearch]         = useState('');
  const [segment, setSegment]       = useState('all');
  const [expanded, setExpanded]     = useState(null);
  const [showNotif, setShowNotif]   = useState(false);
  const [notifTitle, setNotifTitle] = useState('☕ Sizi Özlədik!');
  const [notifBody, setNotifBody]   = useState('Bir müddətdir görüşməmişik. Kafemizə gəlin, sizə xüsusi sürpriz hazırladıq! 🎁');
  const [sending, setSending]       = useState(false);
  const [sentResult, setSentResult] = useState(null);

  const TEMPLATES = [
    { label: 'Xatırlatma',    title: '☕ Sizi Özlədik!',    body: 'Bir müddətdir görüşməmişik. Kafemizə gəlin, xüsusi endirim sizi gözləyir! 🎁' },
    { label: 'Endirim',       title: '🎁 Xüsusi Təklif!',   body: 'Bu həftə kafemizə gəlin — xüsusi endirim sizi gözləyir!' },
    { label: 'Yeni menyu',    title: '🍽️ Yeniliklər Var!', body: 'Kafemizdə yeni ləzzətlər sizi gözləyir, gəlin dadın!' },
  ];

  useEffect(() => { loadCustomers(); }, [business, locationId]);

  const sendSegmentNotification = async () => {
    const targets = filtered.filter(c => c.user_id);
    if (!targets.length) return;
    setSending(true);
    setSentResult(null);
    let success = 0;
    for (const c of targets) {
      const { error } = await supabase.rpc('notify_user', {
        p_user_id: c.user_id,
        p_title:   notifTitle,
        p_body:    notifBody,
        p_data:    { type: 'business_promo', business_id: business.id },
      });
      if (!error) success++;
    }
    setSending(false);
    setSentResult({ success, total: targets.length });
  };

  const loadCustomers = async () => {
    setLoading(true);
    try {
      // 1. Bookings
      let bq = supabase
        .from('flash_bookings')
        .select('*, users(id, full_name, avatar_url)')
        .eq('business_id', business.id)
        .order('created_at', { ascending: false });
      if (locationId) bq = bq.or(`location_id.eq.${locationId},location_id.is.null`);
      const { data: bookingRows } = await bq;

      // 2. QR scan logs
      const { data: scanRows } = await supabase
        .from('point_logs')
        .select('user_id, user_name, created_at, amount_tier')
        .eq('business_id', business.id)
        .order('created_at', { ascending: false });

      // 3. Coupon logs
      const { data: couponRows } = await supabase
        .from('coupon_logs')
        .select('user_id, coupon_title, created_at')
        .eq('business_id', business.id)
        .order('created_at', { ascending: false });

      // Build coupon map keyed by user_id
      const couponMap = {};
      (couponRows || []).forEach(c => {
        if (!c.user_id) return;
        if (!couponMap[c.user_id]) couponMap[c.user_id] = [];
        couponMap[c.user_id].push(c);
      });

      // Build scan map keyed by user_id — full rows stored
      const scanMap = {};
      (scanRows || []).forEach(s => {
        if (!s.user_id) return;
        if (!scanMap[s.user_id]) {
          scanMap[s.user_id] = { scans: 0, lastScan: null, name: s.user_name, rows: [] };
        }
        scanMap[s.user_id].scans++;
        scanMap[s.user_id].rows.push(s);
        if (!scanMap[s.user_id].lastScan || s.created_at > scanMap[s.user_id].lastScan) {
          scanMap[s.user_id].lastScan = s.created_at;
        }
      });

      // Build customer map keyed by user_id (registered) or phone (guest)
      const map = {};
      (bookingRows || []).forEach(b => {
        const key = b.user_id || `g_${b.contact_phone}`;
        if (!map[key]) {
          map[key] = {
            key,
            user_id:     b.user_id,
            name:        b.users?.full_name || b.contact_name || 'Naməlum',
            phone:       b.contact_phone,
            avatar:      b.users?.avatar_url,
            bookings:    [],
            hasBirthday: false,
            scans:       0,
            lastScan:    null,
            scanRows:    [],
            couponRows:  [],
          };
        }
        map[key].bookings.push(b);
        if (b.occasion === 'birthday') map[key].hasBirthday = true;
      });

      // Merge scans into map
      Object.entries(scanMap).forEach(([uid, s]) => {
        if (map[uid]) {
          map[uid].scans    = s.scans;
          map[uid].lastScan = s.lastScan;
          map[uid].scanRows = s.rows;
        } else {
          map[uid] = {
            key:         uid,
            user_id:     uid,
            name:        s.name || 'İstifadəçi',
            phone:       null,
            avatar:      null,
            bookings:    [],
            hasBirthday: false,
            scans:       s.scans,
            lastScan:    s.lastScan,
            scanRows:    s.rows,
            couponRows:  [],
          };
        }
      });

      // Merge coupons into map
      Object.entries(couponMap).forEach(([uid, rows]) => {
        if (map[uid]) map[uid].couponRows = rows;
      });

      // Compute lastSeen = max(lastScan, lastBooking.created_at)
      const result = Object.values(map).map(c => {
        const lastBookingDate = c.bookings[0]?.created_at || null;
        const lastSeen = [c.lastScan, lastBookingDate]
          .filter(Boolean)
          .sort()
          .reverse()[0] || null;
        return { ...c, lastSeen };
      });

      result.sort((a, b) => {
        if (!a.lastSeen) return 1;
        if (!b.lastSeen) return -1;
        return new Date(b.lastSeen) - new Date(a.lastSeen);
      });

      setCustomers(result);
    } catch (err) {
      console.error('CustomersPage:', err);
    } finally {
      setLoading(false);
    }
  };

  const now = new Date();

  const segmentCount = (key) => {
    if (key === 'inactive') return customers.filter(c => !c.lastSeen || (now - new Date(c.lastSeen)) / DAY_MS >= 30).length;
    if (key === 'birthday') return customers.filter(c => c.hasBirthday).length;
    if (key === 'frequent') return customers.filter(c => c.scans >= 3 || c.bookings.length >= 3).length;
    return customers.length;
  };

  const filtered = customers.filter(c => {
    if (search) {
      const q = search.toLowerCase();
      if (!c.name.toLowerCase().includes(q) && !(c.phone || '').includes(q)) return false;
    }
    if (segment === 'inactive') {
      if (!c.lastSeen) return true;
      return (now - new Date(c.lastSeen)) / DAY_MS >= 30;
    }
    if (segment === 'birthday') return c.hasBirthday;
    if (segment === 'frequent') return c.scans >= 3 || c.bookings.length >= 3;
    return true;
  });

  const formatDate = iso => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('az-AZ', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const daysSince = iso => {
    if (!iso) return '—';
    const d = Math.floor((now - new Date(iso)) / DAY_MS);
    if (d === 0) return 'Bu gün';
    if (d === 1) return 'Dünən';
    return `${d} gün əvvəl`;
  };

  if (loading) {
    return (
      <div className="customers-loading">
        <div className="customers-spinner" />
        <p>Yüklənir...</p>
      </div>
    );
  }

  return (
    <div className="customers-page">
      <LocationBanner />

      <div className="customers-header">
        <div>
          <h1>Müştəri Bazası</h1>
          <p>{customers.length} müştəri qeydə alınıb</p>
        </div>
        {segment === 'inactive' && filtered.filter(c => c.user_id).length > 0 && (
          <button
            onClick={() => { setShowNotif(true); setSentResult(null); }}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '10px 18px', borderRadius: 10, border: 'none',
              background: '#E63950', color: '#fff', fontWeight: 700,
              fontSize: 14, cursor: 'pointer', boxShadow: '0 2px 8px rgba(230,57,80,0.3)',
            }}
          >
            <FaBell style={{ fontSize: 13 }} />
            Bildiriş Göndər ({filtered.filter(c => c.user_id).length})
          </button>
        )}
      </div>

      {/* Toolbar: search + segments */}
      <div className="customers-toolbar">
        <div className="customers-search">
          <FaSearch />
          <input
            type="text"
            placeholder="Ad və ya telefon axtar..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <div className="customers-segments">
          {SEGMENTS.map(s => (
            <button
              key={s.key}
              className={`seg-btn${segment === s.key ? ' active' : ''}`}
              onClick={() => setSegment(s.key)}
            >
              {s.label}
              {s.key !== 'all' && (
                <span className="seg-count">{segmentCount(s.key)}</span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* List */}
      {filtered.length === 0 ? (
        <div className="customers-empty">
          <FaUsers />
          <p>Müştəri tapılmadı</p>
        </div>
      ) : (
        <div className="customers-list">
          {filtered.map(c => (
            <div key={c.key} className="customer-card">
              {/* Main row */}
              <div
                className="customer-row"
                onClick={() => setExpanded(expanded === c.key ? null : c.key)}
              >
                <div className="customer-avatar">
                  {c.avatar ? (
                    <img src={av(c.avatar)} alt={c.name} />
                  ) : (
                    <div className="avatar-placeholder">{c.name[0].toUpperCase()}</div>
                  )}
                </div>

                <div className="customer-info">
                  <div className="customer-name">
                    {c.name}
                    {c.hasBirthday && <span className="tag tag-birthday">Doğum günü</span>}
                    {(c.scans >= 3 || c.bookings.length >= 3) && (
                      <span className="tag tag-loyal">Loyal</span>
                    )}
                  </div>
                  {c.phone && (
                    <div className="customer-phone">
                      <FaPhone /> {c.phone}
                    </div>
                  )}
                </div>

                <div className="customer-stats">
                  <div className="cstat"><FaQrcode /><span>{c.scans}</span> scan</div>
                  <div className="cstat"><FaCalendarAlt /><span>{c.bookings.length}</span> rezerv</div>
                </div>

                <div className="customer-last">
                  <div className="last-label">Son görüş</div>
                  <div className="last-value">{daysSince(c.lastSeen)}</div>
                </div>

                <div className="customer-expand">
                  {expanded === c.key ? <FaChevronUp /> : <FaChevronDown />}
                </div>
              </div>

              {/* Expanded: full activity history */}
              {expanded === c.key && (
                <div className="booking-history">

                  {/* QR Scan tarixçəsi */}
                  <div className="bh-section">
                    <div className="bh-title">QR Scan tarixçəsi</div>
                    {c.scanRows.length === 0 ? (
                      <p className="bh-empty">QR scan yoxdur</p>
                    ) : (
                      c.scanRows.map((s, i) => (
                        <div key={i} className="bh-row">
                          <div className="bh-date">{formatDate(s.created_at)}</div>
                          <div className="bh-time">{new Date(s.created_at).toLocaleTimeString('az-AZ', { hour: '2-digit', minute: '2-digit' })}</div>
                          <div
                            className="bh-tier"
                            style={TIER_STYLE[s.amount_tier] || TIER_STYLE['5-20']}
                          >
                            {s.amount_tier} ₼
                          </div>
                        </div>
                      ))
                    )}
                  </div>

                  {/* Kupon tarixçəsi */}
                  <div className="bh-section">
                    <div className="bh-title">Kupon tarixçəsi</div>
                    {c.couponRows.length === 0 ? (
                      <p className="bh-empty">Kupon istifadəsi yoxdur</p>
                    ) : (
                      c.couponRows.map((cp, i) => (
                        <div key={i} className="bh-row">
                          <div className="bh-date">{formatDate(cp.created_at)}</div>
                          <div className="bh-time">{new Date(cp.created_at).toLocaleTimeString('az-AZ', { hour: '2-digit', minute: '2-digit' })}</div>
                          <div className="bh-coupon">{cp.coupon_title || 'Kupon'}</div>
                        </div>
                      ))
                    )}
                  </div>

                  {/* Rezervasiya tarixçəsi */}
                  <div className="bh-section">
                    <div className="bh-title">Rezervasiya tarixçəsi</div>
                    {c.bookings.length === 0 ? (
                      <p className="bh-empty">Rezervasiya yoxdur</p>
                    ) : (
                      c.bookings.map(b => {
                        const st = STATUS_STYLE[b.status] || STATUS_STYLE.pending;
                        return (
                          <div key={b.id} className="bh-row">
                            <div className="bh-date">{formatDate(b.reservation_date || b.created_at)}</div>
                            <div className="bh-time">{b.reservation_time?.slice(0, 5) || '—'}</div>
                            <div className="bh-guests">{b.party_size || b.guest_count || 1} nəfər</div>
                            {b.occasion && (
                              <div className="bh-occasion">
                                {OCCASION_LABELS[b.occasion] || b.occasion}
                              </div>
                            )}
                            <div
                              className="bh-status"
                              style={{ color: st.color, background: st.bg }}
                            >
                              {st.label}
                            </div>
                          </div>
                        );
                      })
                    )}
                  </div>

                </div>
              )}
            </div>
          ))}
        </div>
      )}
      {/* ── Bildiriş Modal ── */}
      {showNotif && (
        <div style={{
          position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)',
          zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: 16,
        }}
          onClick={e => { if (e.target === e.currentTarget) setShowNotif(false); }}
        >
          <div style={{
            background: '#fff', borderRadius: 16, padding: 28,
            width: '100%', maxWidth: 480,
            boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
          }}>
            {/* Modal header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <div>
                <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: '#1a1a2e' }}>
                  🔔 Bildiriş Göndər
                </h2>
                <p style={{ margin: '4px 0 0', fontSize: 13, color: '#94a3b8' }}>
                  {filtered.filter(c => c.user_id).length} müştərinin telefonuna çatacaq
                </p>
              </div>
              <button
                onClick={() => setShowNotif(false)}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8', fontSize: 18 }}
              >
                <FaTimes />
              </button>
            </div>

            {/* Hazır şablonlar */}
            <div style={{ marginBottom: 16 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#94a3b8', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Sürətli şablonlar
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {TEMPLATES.map(t => (
                  <button
                    key={t.label}
                    onClick={() => { setNotifTitle(t.title); setNotifBody(t.body); }}
                    style={{
                      padding: '5px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600,
                      border: '1.5px solid #e5e7eb', background: '#f9fafb',
                      cursor: 'pointer', color: '#374151',
                      transition: 'all 0.15s',
                    }}
                  >
                    {t.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Başlıq */}
            <div style={{ marginBottom: 12 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                Başlıq
              </label>
              <input
                value={notifTitle}
                onChange={e => setNotifTitle(e.target.value)}
                maxLength={60}
                style={{
                  width: '100%', padding: '10px 12px', borderRadius: 8,
                  border: '1.5px solid #e5e7eb', fontSize: 14, outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            {/* Mətn */}
            <div style={{ marginBottom: 20 }}>
              <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#374151', marginBottom: 6 }}>
                Mətn
              </label>
              <textarea
                value={notifBody}
                onChange={e => setNotifBody(e.target.value)}
                maxLength={200}
                rows={3}
                style={{
                  width: '100%', padding: '10px 12px', borderRadius: 8,
                  border: '1.5px solid #e5e7eb', fontSize: 14, outline: 'none',
                  resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box',
                }}
              />
              <div style={{ fontSize: 11, color: '#94a3b8', textAlign: 'right', marginTop: 2 }}>
                {notifBody.length}/200
              </div>
            </div>

            {/* Nəticə */}
            {sentResult && (
              <div style={{
                marginBottom: 16, padding: '10px 14px', borderRadius: 8,
                background: sentResult.success > 0 ? '#f0fdf4' : '#fef2f2',
                border: `1px solid ${sentResult.success > 0 ? '#bbf7d0' : '#fecaca'}`,
                fontSize: 13, color: sentResult.success > 0 ? '#16a34a' : '#dc2626', fontWeight: 600,
              }}>
                {sentResult.success > 0
                  ? `✅ ${sentResult.success} müştəriyə bildiriş göndərildi!`
                  : '❌ Bildiriş göndərilə bilmədi'}
              </div>
            )}

            {/* Düymələr */}
            <div style={{ display: 'flex', gap: 10 }}>
              <button
                onClick={() => setShowNotif(false)}
                style={{
                  flex: 1, padding: '11px', borderRadius: 9, border: '1.5px solid #e5e7eb',
                  background: '#fff', color: '#374151', fontWeight: 600, fontSize: 14, cursor: 'pointer',
                }}
              >
                Ləğv et
              </button>
              <button
                onClick={sendSegmentNotification}
                disabled={sending || !notifTitle.trim() || !notifBody.trim()}
                style={{
                  flex: 2, padding: '11px', borderRadius: 9, border: 'none',
                  background: sending ? '#f5a0ab' : '#E63950', color: '#fff',
                  fontWeight: 700, fontSize: 14, cursor: sending ? 'not-allowed' : 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                }}
              >
                <FaPaperPlane style={{ fontSize: 12 }} />
                {sending ? 'Göndərilir...' : `Göndər (${filtered.filter(c => c.user_id).length} nəfər)`}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomersPage;
